import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { downloadBlob } from "@/lib/resumeUtils";
import { UploadCloud, Trash2, Check, RefreshCw, Mail, Send, CreditCard, FileDown, XCircle, Sparkles, LogOut } from "lucide-react";
import { toast } from "sonner";
import CancelAccountModal from "@/components/CancelAccountModal";
import CancelChoiceModal from "@/components/CancelChoiceModal";
import AffiliatePanel from "@/components/AffiliatePanel";
import TestimonialsSection from "@/components/TestimonialsSection";
import TwoFactorPanel from "@/components/TwoFactorPanel";
import MatchAlertPrefsPanel from "@/components/MatchAlertPrefsPanel";
import SkipSuggestionsPanel from "@/components/SkipSuggestionsPanel";

// Ordered personal-info fields shown on the profile form. Address is now
// split into structured lines (street + number, postal code, city, country)
// so the CV PDF can format it Nederlandse-post-style — but the legacy
// single-line `address` stays behind the scenes for backwards compat.
const FIELDS = [
  ["full_name", "rbFullName"],
  ["address_street", "profAddressStreet"],
  ["address_number", "profAddressNumber"],
  ["postal_code", "profPostalCode"],
  ["city", "profCity"],
  ["country", "profCountry"],
  ["birthdate", "ppBirthdate"],
  ["nationality", "rbNationality"],
  ["phone", "rbPhone"],
  ["email", "rbEmail"],
  ["linkedin", "rbLinkedin"],
];

export default function ProfilePanel() {
  const { t } = useT();
  const { user, setUser } = useAuth();
  const [personal, setPersonal] = useState(null);
  const [photo, setPhoto] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [digestOn, setDigestOn] = useState(null);
  const [followupsOn, setFollowupsOn] = useState(null);
  const [trashReminderOn, setTrashReminderOn] = useState(null);
  const [archiveExpiryOn, setArchiveExpiryOn] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState(null);
  const photoRef = useRef(null);
  const pRef = useRef(null);
  pRef.current = personal;

  useEffect(() => {
    api.get("/profile/personal")
      .then(({ data }) => { setPersonal(data.personal); setPhoto(data.photo || ""); })
      .catch(() => toast.error(t("dashLoadFailed")));
    api.get("/emails/prefs")
      .then(({ data }) => {
        setDigestOn(!!data.weekly_digest);
        setFollowupsOn(data.followup_reminders !== false);
        setTrashReminderOn(data.trash_reminders !== false);
        setArchiveExpiryOn(data.archive_expiry !== false);
      })
      .catch(() => {});
    api.get("/billing/me")
      .then(({ data }) => setSub(data))
      .catch(() => {});
    api.get("/billing/invoices")
      .then(({ data }) => setInvoices(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [cancelModal, setCancelModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() =>
    searchParams.get("tab") === "affiliate" ? "affiliate" : "profile"
  );

  const openCancelModal = () => setCancelModal(true);
  const closeCancelModal = () => setCancelModal(false);

  const cancelSub = openCancelModal;

  const downloadInvoice = async (inv) => {
    setDlBusy(inv.id);
    try {
      const res = await api.get(`/billing/invoices/${inv.id}/pdf`, { responseType: "blob" });
      downloadBlob(new Blob([res.data], { type: "application/pdf" }), `factuur_${inv.invoice_no}.pdf`);
    } catch {
      toast.error(t("rbSaveFailed"));
    } finally {
      setDlBusy(null);
    }
  };

  const toggleDigest = async () => {
    const next = !digestOn;
    setDigestOn(next);
    try {
      await api.put("/emails/prefs", { weekly_digest: next, followup_reminders: !!followupsOn, trash_reminders: !!trashReminderOn, archive_expiry: !!archiveExpiryOn });
      toast.success(next ? t("ppDigestOn") : t("ppDigestOff"));
    } catch {
      setDigestOn(!next);
      toast.error(t("rbSaveFailed"));
    }
  };

  const toggleFollowups = async () => {
    const next = !followupsOn;
    setFollowupsOn(next);
    try {
      await api.put("/emails/prefs", { weekly_digest: !!digestOn, followup_reminders: next, trash_reminders: !!trashReminderOn, archive_expiry: !!archiveExpiryOn });
      toast.success(next ? t("ppFollowupsOn") : t("ppFollowupsOff"));
    } catch {
      setFollowupsOn(!next);
      toast.error(t("rbSaveFailed"));
    }
  };

  const toggleTrashReminders = async () => {
    const next = !trashReminderOn;
    setTrashReminderOn(next);
    try {
      await api.put("/emails/prefs", { weekly_digest: !!digestOn, followup_reminders: !!followupsOn, trash_reminders: next, archive_expiry: !!archiveExpiryOn });
      toast.success(next ? t("ppTrashRemindersOn") : t("ppTrashRemindersOff"));
    } catch {
      setTrashReminderOn(!next);
      toast.error(t("rbSaveFailed"));
    }
  };

  const toggleArchiveExpiry = async () => {
    const next = !archiveExpiryOn;
    setArchiveExpiryOn(next);
    try {
      await api.put("/emails/prefs", { weekly_digest: !!digestOn, followup_reminders: !!followupsOn, trash_reminders: !!trashReminderOn, archive_expiry: next });
      toast.success(next ? t("ppArchiveExpiryOn") : t("ppArchiveExpiryOff"));
    } catch {
      setArchiveExpiryOn(!next);
      toast.error(t("rbSaveFailed"));
    }
  };

  const sendSample = async () => {
    setSampleBusy(true);
    try {
      await api.post("/emails/weekly-digest/test");
      toast.success(t("ppSampleSent"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ppSampleFailed"));
    } finally {
      setSampleBusy(false);
    }
  };

  const save = useCallback(async () => {
    const cur = pRef.current;
    if (!cur) return;
    setSaving(true);
    try {
      await api.put("/profile/personal", cur);
      if (pRef.current === cur) setDirty(false);
    } catch {
      toast.error(t("rbSaveFailed"));
    } finally {
      setSaving(false);
    }
  }, [t]);

  useEffect(() => {
    if (!dirty || !personal) return;
    const timer = setTimeout(() => { save(); }, 1200);
    return () => clearTimeout(timer);
  }, [personal, dirty, save]);

  const update = (field, value) => {
    setPersonal((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/photo", fd);
      setPhoto(data.photo);
      toast.success(t("rbPhotoUploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbSaveFailed"));
    } finally {
      setPhotoBusy(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    try {
      await api.delete("/profile/photo");
      setPhoto("");
      toast.success(t("rbPhotoRemoved"));
    } catch {
      toast.error(t("rbSaveFailed"));
    }
  };

  if (!personal) return <p className="ed-eyebrow p-2">{t("loading")}</p>;

  return (
    <section data-testid="profile-panel" className="max-w-3xl">
      <p className="ed-eyebrow">{t("navProfile")}</p>
      <h1 className="ed-h1 mt-4 mb-2">{t("ppTitle")}</h1>
      <p className="text-sm text-neutral-600 font-serif italic mb-6">{t("ppHint")}</p>

      {/* Tab strip — keeps existing profile content untouched, adds Affiliate as a peer tab */}
      <div className="flex border border-neutral-300 mb-6 w-fit">
        {[["profile", t("navProfile")], ["affiliate", t("affTabLabel")]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`profile-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-5 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${
              tab === k ? "bg-neutral-900 text-[#F9F8F6]" : "hover:bg-neutral-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "affiliate" ? <AffiliatePanel /> : (
      <>

      <div className="ed-card p-6 mb-6">
        <div className="flex items-start gap-5">
          <div className="w-24 h-24 border border-neutral-300 bg-white overflow-hidden flex items-center justify-center shrink-0" data-testid="profile-photo-preview">
            {photo ? (
              <img src={photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <UploadCloud size={22} strokeWidth={1.2} className="text-neutral-400" />
            )}
          </div>
          <div className="min-w-0">
            <p className="ed-label">{t("rbPhoto")}</p>
            <div className="flex flex-wrap gap-2 mt-1">
              <input
                ref={photoRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="profile-photo-input"
                onChange={(e) => uploadPhoto(e.target.files?.[0])}
              />
              <button data-testid="profile-photo-upload" className="ed-btn ed-btn-outline" onClick={() => photoRef.current?.click()} disabled={photoBusy}>
                <UploadCloud size={14} strokeWidth={1.5} /> {photoBusy ? t("cvUploading") : (photo ? t("cvReplace") : t("rbPhotoUpload"))}
              </button>
              {photo && (
                <button data-testid="profile-photo-remove" className="ed-btn ed-btn-outline" onClick={removePhoto} title={t("rbPhotoRemove")}>
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
            <p className="text-[0.65rem] font-mono text-neutral-500 mt-2">{t("ppPhotoHint")}</p>
          </div>
        </div>
      </div>

      <div className="ed-card p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="ed-eyebrow">{t("ppData")}</p>
          <span
            data-testid="profile-autosave-status"
            className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] inline-flex items-center gap-1.5 ${saving || dirty ? "text-neutral-500" : "text-emerald-700"}`}
          >
            {saving ? (
              <><RefreshCw size={11} strokeWidth={1.5} className="animate-spin" /> {t("rbAutosaving")}</>
            ) : dirty ? (
              t("rbAutosavePending")
            ) : (
              <><Check size={11} strokeWidth={2} /> {t("rbAutosaved")}</>
            )}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([field, labelKey]) => (
            <div key={field}>
              <label className="ed-label">{t(labelKey)}</label>
              <input
                data-testid={`profile-${field}`}
                className="ed-input"
                placeholder={field === "birthdate" ? t("ppBirthdatePh") : ""}
                value={personal[field] || ""}
                onChange={(e) => update(field, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="ed-card p-6" data-testid="email-prefs-card">
        <p className="ed-eyebrow flex items-center gap-2 mb-2">
          <Mail size={12} strokeWidth={1.5} /> {t("ppEmailPrefs")}
        </p>
        <p className="text-xs font-mono text-neutral-500">{t("ppDigestHint")}</p>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button
            data-testid="digest-toggle"
            onClick={toggleDigest}
            disabled={digestOn === null}
            className={`flex items-center gap-2 border px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${digestOn ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
          >
            <span className={`w-3.5 h-3.5 border flex items-center justify-center ${digestOn ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
              {digestOn && <Check size={10} strokeWidth={3} />}
            </span>
            {t("ppDigestLabel")}
          </button>
          <button
            data-testid="followups-toggle"
            onClick={toggleFollowups}
            disabled={followupsOn === null}
            className={`flex items-center gap-2 border px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${followupsOn ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
          >
            <span className={`w-3.5 h-3.5 border flex items-center justify-center ${followupsOn ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
              {followupsOn && <Check size={10} strokeWidth={3} />}
            </span>
            {t("ppFollowupsLabel")}
          </button>
          <button
            data-testid="trash-reminders-toggle"
            onClick={toggleTrashReminders}
            disabled={trashReminderOn === null}
            className={`flex items-center gap-2 border px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${trashReminderOn ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
          >
            <span className={`w-3.5 h-3.5 border flex items-center justify-center ${trashReminderOn ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
              {trashReminderOn && <Check size={10} strokeWidth={3} />}
            </span>
            {t("ppTrashRemindersLabel")}
          </button>
          <button
            data-testid="archive-expiry-toggle"
            onClick={toggleArchiveExpiry}
            disabled={archiveExpiryOn === null}
            className={`flex items-center gap-2 border px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${archiveExpiryOn ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
            title={t("ppArchiveExpiryHint")}
          >
            <span className={`w-3.5 h-3.5 border flex items-center justify-center ${archiveExpiryOn ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
              {archiveExpiryOn && <Check size={10} strokeWidth={3} />}
            </span>
            {t("ppArchiveExpiryLabel")}
          </button>
          <button
            data-testid="digest-sample-btn"
            className="ed-btn ed-btn-outline"
            onClick={sendSample}
            disabled={sampleBusy}
          >
            <Send size={13} strokeWidth={1.5} /> {sampleBusy ? t("emwSending") : t("ppSendSample")}
          </button>
        </div>

        {/* Ticket notifications + follow-up settings */}
        <div className="mt-5 pt-5 border-t border-neutral-100 space-y-3">
          <button
            data-testid="notify-ticket-toggle"
            onClick={async () => {
              const next = !(user?.notify_ticket_updates ?? true);
              try {
                const { data } = await api.patch("/auth/preferences", { notify_ticket_updates: next });
                setUser(data);
                toast.success(next ? t("ppTicketNotifyOn") : t("ppTicketNotifyOff"));
              } catch { toast.error(t("rbSaveFailed")); }
            }}
            className={`flex items-center gap-2 border px-3 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors ${user?.notify_ticket_updates ? "border-neutral-900 bg-white" : "border-neutral-300 hover:border-neutral-500"}`}
          >
            <span className={`w-3.5 h-3.5 border flex items-center justify-center ${user?.notify_ticket_updates ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-400"}`}>
              {user?.notify_ticket_updates && <Check size={10} strokeWidth={3} />}
            </span>
            {t("ppTicketNotifyLabel")}
          </button>

          <label className="block max-w-xs">
            <span className="ed-label mb-1 flex items-center gap-2">{t("ppFollowupDaysLabel")}</span>
            <input
              type="number"
              min={1} max={90}
              data-testid="followup-default-days"
              value={user?.followup_default_days ?? 10}
              onChange={async (e) => {
                const v = Math.max(1, Math.min(90, Number(e.target.value) || 10));
                try {
                  const { data } = await api.patch("/auth/preferences", { followup_default_days: v });
                  setUser(data);
                } catch { toast.error(t("rbSaveFailed")); }
              }}
              className="ed-input w-24"
            />
            <span className="text-[0.6rem] font-mono text-neutral-500 mt-1 block">{t("ppFollowupDaysHint")}</span>
          </label>
        </div>
      </div>

      <div className="mt-6">
        <TwoFactorPanel />
      </div>

      <div className="mt-6">
        <MatchAlertPrefsPanel />
      </div>

      <div className="mt-6">
        <SkipSuggestionsPanel />
      </div>

      <div className="mt-6">
        <TestimonialsSection />
      </div>

      <div className="ed-card p-6 mt-6" data-testid="membership-card">
        <p className="ed-eyebrow flex items-center gap-2 mb-2">
          <CreditCard size={12} strokeWidth={1.5} /> {t("ppMembership")}
        </p>
        {!sub ? (
          <p className="ed-eyebrow mt-3">{t("loading")}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div>
                <p className="ed-label">{t("ppMemStatusLabel")}</p>
                <p data-testid="membership-status" className="font-serif text-lg mt-1">
                  {sub.is_admin ? t("ppMemAdmin")
                    : sub.subscription_status === "active" ? t("ppMemActive")
                    : sub.subscription_status === "trial" ? t("ppMemTrial")
                    : t("ppMemExpired")}
                </p>
              </div>
              <div>
                <p className="ed-label">{t("ppMemPlanLabel")}</p>
                <p data-testid="membership-plan" className="font-serif text-lg mt-1">
                  {sub.is_admin ? t("ppMemAdminPlan")
                    : sub.subscription_status === "active"
                    ? (sub.plan === "yearly" ? t("ppMemPlanYearly")
                      : sub.plan === "monthly" ? t("ppMemPlanMonthly")
                      : (sub.plan || "—"))
                    : sub.subscription_status === "trial" ? t("ppMemPlanTrial") : "—"}
                </p>
              </div>
              <div>
                <p className="ed-label">{t("ppMemUntil")}</p>
                <p data-testid="membership-until" className="font-serif text-lg mt-1">
                  {sub.is_admin ? "∞" : ((sub.subscription_status === "active" ? sub.subscription_expires_at : sub.trial_ends_at) || "").slice(0, 10) || "—"}
                  {!sub.is_admin && sub.days_left > 0 && (
                    <span className="font-mono text-[0.65rem] text-neutral-500 ml-2">{t("ppMemDaysLeft", { days: sub.days_left })}</span>
                  )}
                </p>
              </div>
            </div>

            {sub.cancelled && sub.subscription_status === "active" && (
              <p data-testid="membership-cancelled-note" className="mt-4 font-mono text-xs text-amber-700">{t("ppMemCancelledNote")}</p>
            )}

            <div className="flex flex-wrap gap-2 mt-5">
              {sub.subscription_status === "active" && !sub.cancelled && (
                <button data-testid="membership-cancel-btn" className="ed-btn ed-btn-outline" onClick={cancelSub} disabled={cancelBusy}>
                  <XCircle size={13} strokeWidth={1.5} /> {cancelBusy ? "…" : t("ppMemCancelBtn")}
                </button>
              )}
              {sub.subscription_status !== "active" && !sub.is_admin && (
                <Link to="/pricing" data-testid="membership-upgrade-btn" className="ed-btn">
                  <Sparkles size={13} strokeWidth={1.5} /> {t("ppMemUpgrade")}
                </Link>
              )}
            </div>
            <p className="text-[0.65rem] font-mono text-neutral-500 mt-3">{t("ppMemCancelHint")}</p>
          </>
        )}

        <div className="mt-6 border-t border-neutral-200 pt-5" data-testid="invoices-section">
          <p className="ed-label">{t("ppInvoices")}</p>
          <p className="text-[0.65rem] font-mono text-neutral-500 mt-1">{t("ppInvoicesHint")}</p>
          {invoices.length === 0 ? (
            <p className="text-sm text-neutral-500 italic font-serif mt-3" data-testid="no-invoices">{t("ppNoInvoices")}</p>
          ) : (
            <div className="mt-3 space-y-2">
              {invoices.map((inv, i) => (
                <div key={inv.id} data-testid={`invoice-row-${i}`} className="flex items-center justify-between gap-3 border border-neutral-200 bg-white px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="font-mono text-xs">{inv.invoice_no}</p>
                    <p className="font-mono text-[0.6rem] text-neutral-500 mt-0.5">
                      {(inv.created_at || "").slice(0, 10)} · {inv.plan === "yearly" ? t("priceYearly") : t("priceMonthly")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-serif">€ {Number(inv.amount).toFixed(2).replace(".", ",")}</span>
                    <button data-testid={`invoice-dl-${i}`} className="ed-btn ed-btn-outline" onClick={() => downloadInvoice(inv)} disabled={dlBusy === inv.id}>
                      <FileDown size={13} strokeWidth={1.5} /> {dlBusy === inv.id ? "…" : t("ppInvoiceDl")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Danger zone — account cancellation & deletion. */}
      <div className="ed-card p-6 border-rose-300" data-testid="danger-zone">
        <p className="ed-eyebrow text-rose-700">{t("dangerZoneEyebrow")}</p>
        <h3 className="font-serif text-2xl mt-1">{t("dangerZoneTitle")}</h3>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl leading-relaxed">
          {t("dangerZoneBody")}
        </p>
        <button
          data-testid="cancel-account-button"
          onClick={() => setCancelOpen(true)}
          className="ed-btn mt-4 border-rose-700 text-rose-700 hover:bg-rose-700 hover:text-white"
        >
          <LogOut size={13} strokeWidth={1.7} /> {t("cancelAccountCta")}
        </button>
      </div>

      </>
      )}

      {cancelModal && (
        <CancelChoiceModal
          sub={sub}
          onClose={closeCancelModal}
          onCancelled={() => setSub((s) => ({ ...s, cancelled: true }))}
          onOpenDelete={() => setCancelOpen(true)}
        />
      )}
      {cancelOpen && <CancelAccountModal onClose={() => setCancelOpen(false)} />}
    </section>
  );
}
